import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CruiseLuxPlusFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseLuxPlusFilter'
import type {
  CruisePriceFilterChangeProps,
} from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePriceFilter'
import CruisePriceFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePriceFilter'
import CruisePromotionFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePromotionFilter'
import CruiseShipFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseShipFilter'
import CruiseCruiseTypeFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseCruiseTypeFilter'
import Group from 'components/utils/Group'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import useCruiseFacetFiltersParams from 'hooks/useCruiseFacetFiltersParams'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { without } from 'lib/array/arrayUtils'
import { getCruiseLuxPlusItems } from 'lib/cruises/cruiseUtils'
import { semiFormToObject } from 'lib/forms/formUtils'
import noop from 'lib/function/noop'
import debounce from 'lodash/debounce'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { useCallback, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { LUX_PLUS_MONTH_CAMPAIGN_ID, LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'

function toTitleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function buildPanelCheckItems(
  facets: Array<App.CruiseSearchFacet>,
  searchString?: string,
) {
  if (facets.length === 0) return []

  return facets.map((item): FilterPanelCheckItem => {
    const isCruiseType = item.category === 'cruise_types'
    const displayName = isCruiseType ? toTitleCase(item.name) : item.name
    const value = item.value ?? item.name

    return {
      value,
      hidden: !!(searchString && facets.length > 5 && !item.name.toLowerCase().includes(searchString.toLowerCase())),
      label: (
        <Group direction="horizontal" horizontalAlign="space-between">
          {displayName}
          <span><FormattedMessage
            id="arX42MPD0JqMrUrbZ2wr"
            defaultMessage="({count})"
            description="Count of matching cruise offers for a filter option in cruise search"
            values={{ count: (item.count || 0) + (item.flashOfferCount || 0) }}
          />
          </span>
        </Group>
      ),
    }
  })
}

export function getCruiseSearchFormValues(formValues: Record<string, any>): Record<string, any> {
  delete formValues.price

  if (formValues.isLuxExclusivePromotion) {
    formValues.isLuxExclusivePromotion = true
  }

  if (formValues.isSpecialOffers) {
    formValues.isSpecialOffers = true
  }

  if (formValues.isLuxPlus) {
    formValues.isLuxPlus = true
  }

  /**
   * Campaign Filters
   */
  if (formValues.campaigns?.includes(LUX_PLUS_MONTH_CAMPAIGN_ID)) {
    formValues.campaigns = [...(formValues.campagins ?? []), LUX_PLUS_MONTH_CAMPAIGN_ID]
  } else if (Array.isArray(formValues.campaigns)) {
    formValues.campaigns = without(formValues.campaigns, LUX_PLUS_MONTH_CAMPAIGN_ID)
  } else if (typeof formValues.campaigns === 'string') {
    formValues.campaigns = formValues.campaigns.replace(LUX_PLUS_MONTH_CAMPAIGN_ID, '')
  } else {
    formValues.campaigns = undefined
  }
  // there is no true 'loyalty' filter (yet), always clear it
  formValues.loyalty = undefined

  return formValues
}

interface Props {
  listFilters: App.OfferListFilters
  onChange?: (values: Record<string, any>) => void
  shouldHidePromotionFilters?: boolean
  shouldHideCruiseShips?: boolean
}

function CruiseSearchFiltersInputs(props: Props) {
  const intl = useIntl()
  const {
    listFilters,
    onChange = noop,
    shouldHidePromotionFilters = false,
    shouldHideCruiseShips = false,
  } = props

  const containerRef = useRef<HTMLDivElement | null>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const cruisePriceByNight = useCruisePriceByNight()
  const { facetParams, queryParamsFilters: filters, mappedFilters } = useCruiseFacetFiltersParams({ filters: listFilters })

  const priceFilterTitle = useMemo(
    () =>
      cruisePriceByNight
        ? intl.formatMessage({
            id: 'Rrn5c7xUpvI3QXdsjN5B',
            defaultMessage: 'Price per night',
            description: 'Title for the price filter group in cruise search when per-night mode is selected',
          })
        : intl.formatMessage({
            id: 'xQZtprOe0VAZoG-9XUlN',
            defaultMessage: 'Price per person',
            description: 'Title for the price filter group in cruise search when full price mode is selected',
          }),
    [cruisePriceByNight, intl],
  )

  const [facets, fetchingFacets] = useCruiseSearchFacets(facetParams)
  const getFormValues = useCallback(() => {
    const formValues = semiFormToObject<Record<string, any>>(containerRef.current!)
    return getCruiseSearchFormValues(formValues)
  }, [])

  const onFilterChange = useCallback(() => {
    const formValues = getFormValues()
    onChange(formValues)
  }, [onChange, getFormValues])

  const onResetPrices = useCallback(() => {
    const formValues = getFormValues()
    onChange({ ...formValues, minPrice: undefined, maxPrice: undefined })
  }, [onChange, getFormValues])

  const onDelayedFilterChange = useMemo(() => debounce(onFilterChange, 500), [onFilterChange])

  const onPriceFilterChange = useCallback((priceFilters: CruisePriceFilterChangeProps) => {
    const formValues = getFormValues()
    onChange({ ...formValues, ...priceFilters })
  }, [getFormValues, onChange])

  const isLuxPlusItemsExists = useMemo(() => {
    const luxPlusItems = getCruiseLuxPlusItems(facets)
    return luxPlusItems.some(item => item.count! > 0)
  }, [facets])

  const isLuxPlusMonthTakeoverEnabled = useCurrentSiteTakeover({ vertical: 'cruise' })?.id === LUX_PLUS_MONTH_TAKEOVER_ID

  const campaignFacets = useMemo(() => {
    /**
     * We have to manually construct our triple point loyalty filter as it's based off a certain campaign, not an actual 'loyalty' facet
     */
    const lpMonthFacet = facets.find(facet => facet.category === 'campaigns' && facet.name === LUX_PLUS_MONTH_CAMPAIGN_ID)
    if (isLuxPlusMonthTakeoverEnabled && lpMonthFacet) {
      return buildPanelCheckItems([{
        name: intl.formatMessage({
          id: 'VsFahg3T6CAe_R9jKKH6',
          defaultMessage: 'LuxPlus+ month',
          description: 'Campaign filter label for LuxPlus+ Month in the cruise search sidebar filters',
        }),
        value: LUX_PLUS_MONTH_CAMPAIGN_ID,
        category: 'campaigns',
        count: lpMonthFacet.count,
      }])
    }
    return []
  }, [facets, intl, isLuxPlusMonthTakeoverEnabled])

  return <VerticalSpacer gap={24} ref={containerRef}>
    {fetchingFacets && <>
      <FilterPanelLoader type="checkbox" />
      <FilterPanelLoader type="checkbox" />
      {isMobile && <FilterPanelLoader type="checkbox" />}
    </>}

    {(!fetchingFacets || isMobile) && <>
      {isLuxPlusItemsExists && <FilterPanelFilterGroup
        title={<LuxPlusLabel type="only-logo" />}
        onReset={onFilterChange}
      >
        {campaignFacets.length > 0 &&
          <FilterPanelCheckboxGroup
            name="campaigns"
            items={campaignFacets}
            onChange={onDelayedFilterChange}
            defaultSelected={filters.campaigns}
          />}
        <CruiseLuxPlusFilter
          isLuxPlus={filters.isLuxPlus}
          onChange={onDelayedFilterChange}
          facets={facets}
        />
      </FilterPanelFilterGroup>}

      {!shouldHidePromotionFilters && <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'DNdCBIQ7a4ugvGygaFXh',
          defaultMessage: 'Promotions',
          description: 'Title for the promotions filter group in cruise search',
        })}
        canReset={!!filters.promotions?.size}
        onReset={onFilterChange}
      >
        <CruisePromotionFilter
          facets={facets}
          isLuxExclusivePromotion={filters.isLuxExclusivePromotion}
          isSpecialOffers={filters.isSpecialOffers}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>}

      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'Q1KZ-ZjKHYqeE8Y3m8CM',
          defaultMessage: 'Cruise type',
          description: 'Title for the cruise type filter group in cruise search',
        })}
        canReset={!!filters.cruiseTypes?.size}
        onReset={onFilterChange}
      >
        <CruiseCruiseTypeFilter
          cruiseTypes={filters.cruiseTypes}
          facets={facets}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>

      <FilterPanelFilterGroup
        title={priceFilterTitle}
        canReset={!!filters.price?.min || !!filters.price?.max}
        onReset={onResetPrices}
      >
        <CruisePriceFilter
          filters={mappedFilters}
          onChange={onPriceFilterChange}
          facets={facets}
        />
      </FilterPanelFilterGroup>
      {!shouldHideCruiseShips && <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'SFDxc3kmdQ1Kc47YuFNr',
          defaultMessage: 'Cruise ships',
          description: 'Title for the cruise ships filter group in cruise search',
        })}
        canReset={!!filters.cruiseShips?.size}
        onReset={onFilterChange}
      >
        <CruiseShipFilter
          cruiseShips={filters.cruiseShips}
          facets={facets}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>}
    </>}
  </VerticalSpacer>
}

export default CruiseSearchFiltersInputs
